import React, { useState } from "react";
import Loader from "../components/Loader";
import PolicyHolder from "../containers/quoteSteps/PolicyHolder";
import VehicleDetails from "../containers/quoteSteps/VehicleDetails";
import ResidentialAddress from "../containers/quoteSteps/ResidentialAddress";
import NightParking from "../containers/quoteSteps/NightParking";
import DayParking from "../containers/quoteSteps/DayParking";
import RegularDriver from "../containers/quoteSteps/RegularDriver";
import License from "../containers/quoteSteps/License";
import Consent from "../containers/quoteSteps/Consent";
import Summary from "../containers/quoteSteps/Summary";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getStatics } from "../api/StaticsApi";
import { useNavigate, useParams } from "react-router-dom";
import Stepper from "../components/Stepper";
import CustomButton from "../components/CustomButton";
import CustomDivider from "../components/CustomDivider";
import { useRecoilValue } from "recoil";
import {
  dayParkingState,
  licenseState,
  nightParkingState,
  policyHolderState,
  regularDriverState,
  residentialAddressState,
  vehicleDetailsState,
} from "../state/QuoteState";
import { createPayload } from "../utils/Payload";
import { createQuote } from "../api/QuoteApi";
import Alert from "../components/Alert";

const Quote = () => {
  const [activePage, setActivePage] = useState(1);
  const [activeFormValid, setActiveFormValid] = useState(false);
  const [showQuoteCreationError, setShowQuoteCreationError] = useState(false);

  const navigate = useNavigate();

  const { customerId } = useParams();
  const policyHolder = useRecoilValue(policyHolderState);
  const vehicleDetails = useRecoilValue(vehicleDetailsState);
  const residentialAddress = useRecoilValue(residentialAddressState);
  const dayParking = useRecoilValue(dayParkingState);
  const nightParking = useRecoilValue(nightParkingState);
  const regularDriver = useRecoilValue(regularDriverState);
  const license = useRecoilValue(licenseState);

  const { data: statics, isPending } = useQuery({
    queryKey: ["statics"],
    queryFn: getStatics,
  });

  const createQuoteQuery = useMutation({
    mutationFn: createQuote,
  });

  const onCreateQuote = () => {
    const payload = createPayload(
      policyHolder,
      vehicleDetails,
      residentialAddress,
      nightParking,
      dayParking,
      regularDriver,
      license,
      customerId,
      statics
    );

    createQuoteQuery.mutate(payload, {
      onSuccess: (data) => {
        console.log(data);
        navigate("/quote", {
          state: { data: data, customerId: customerId },
        });
      },
      onError: (e) => setShowQuoteCreationError(true),
    });
  };

  return (
    <>
      {(isPending || createQuoteQuery.isPending) && (
        <div className="min-w-screen min-h-screen flex align-middle items-center">
          <Loader className="m-auto" />
        </div>
      )}
      {!isPending && (
        <>
          <div className="px-5 pt-28 relative">
            <Stepper active={activePage + 1} total={9} />
            {activePage === 1 && (
              <PolicyHolder setIsFormValid={setActiveFormValid} />
            )}
            {activePage === 2 && (
              <VehicleDetails setIsFormValid={setActiveFormValid} />
            )}
            {activePage === 3 && (
              <ResidentialAddress setIsFormValid={setActiveFormValid} />
            )}
            {activePage === 4 && (
              <NightParking setIsFormValid={setActiveFormValid} />
            )}
            {activePage === 5 && (
              <DayParking setIsFormValid={setActiveFormValid} />
            )}
            {activePage === 6 && (
              <RegularDriver setIsFormValid={setActiveFormValid} />
            )}
            {activePage === 7 && (
              <License setIsFormValid={setActiveFormValid} />
            )}
            {activePage === 8 && (
              <Summary onEdit={(page: number) => setActivePage(page)} />
            )}
            {activePage === 9 && (
              <Consent setIsFormValid={setActiveFormValid} />
            )}
          </div>

          {showQuoteCreationError && (
            <Alert
              text="Error while creating a quote. Please try again, or talk to your salesman!"
              onClose={() => setShowQuoteCreationError(false)}
            />
          )}

          <div className="w-full bg-white">
            <CustomDivider />
            <div className="grid grid-flow-col gap-4 p-5">
              {activePage !== 1 && (
                <CustomButton
                  onClick={() => setActivePage((prev) => prev - 1)}
                  secondary
                >
                  Back
                </CustomButton>
              )}
              {activePage === 9 ? (
                <CustomButton
                  disabled={!activeFormValid}
                  onClick={onCreateQuote}
                >
                  Submit request
                </CustomButton>
              ) : (
                <CustomButton
                  disabled={!activeFormValid}
                  onClick={() => setActivePage((prev) => prev + 1)}
                >
                  Next
                </CustomButton>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Quote;
